import React, {useEffect, useMemo, useState} from 'react';
import './fieldReport.scss';

const isOOS = (vehicle) => (vehicle.vehicle_status_name || '').toLowerCase().includes('out of service');

const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': 'Token ' + process.env.REACT_APP_FLEETIO_AUTHORIZATION_TOKEN,
    'Account-Token': process.env.REACT_APP_FLEETIO_ACCOUNT_TOKEN
};

export default function FieldReport({vehicles, issues, onIssueCreated, onVehicleStatusChanged}) {
    const [assetQuery, setAssetQuery] = useState('');
    const [assetFocused, setAssetFocused] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [title, setTitle] = useState('');
    const [titleFocused, setTitleFocused] = useState(false);
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null); // {type: 'success'|'error', message, url?}
    const [oosStatus, setOosStatus] = useState(null);

    // Find this account's "Out of Service" status so we can set it on the vehicle
    useEffect(() => {
        async function fetchStatuses() {
            try {
                const response = await fetch('https://secure.fleetio.com/api/v1/vehicle_statuses', { headers: apiHeaders });
                const body = await response.json();
                const statuses = Array.isArray(body) ? body : (body && body.records) || [];
                const match = statuses.find((status) => status.name === 'Out of Service')
                    || statuses.find((status) => (status.name || '').toLowerCase().startsWith('out of service'));
                setOosStatus(match || null);
            } catch (error) {
                console.error('Failed to fetch vehicle statuses', error);
            }
        }
        fetchStatuses();
    }, []);

    // Count how often each issue title has been used before, for ranked suggestions.
    // Breakdown/collision issues are excluded - those are created automatically by
    // another system, so their titles aren't useful suggestions here.
    const titleCounts = useMemo(() => {
        const counts = {};
        (issues || []).forEach((issue) => {
            if (issue.custom_fields && issue.custom_fields.breakdown_or_accident) return;
            const issueTitle = issue.summary || issue.name;
            if (!issueTitle) return;
            if (/^(breakdown|collision|accident)\b/i.test(issueTitle.trim())) return;
            counts[issueTitle] = (counts[issueTitle] || 0) + 1;
        });
        return counts;
    }, [issues]);

    const titleSuggestions = useMemo(() => {
        const query = title.trim().toLowerCase();
        return Object.keys(titleCounts)
            .filter((name) => name.toLowerCase().includes(query) && name !== title)
            .sort((a, b) => titleCounts[b] - titleCounts[a])
            .slice(0, 8);
    }, [titleCounts, title]);

    const assetMatches = useMemo(() => {
        if (!vehicles) return [];
        const query = assetQuery.trim().toLowerCase();
        return vehicles
            .filter((vehicle) => vehicle.name.toLowerCase().includes(query))
            .sort((a, b) => isOOS(b) - isOOS(a))
            .slice(0, 12);
    }, [vehicles, assetQuery]);

    function selectVehicle(vehicle) {
        setSelectedVehicle(vehicle);
        setAssetQuery(vehicle.name);
        setResult(null);
    }

    function handleAssetChange(value) {
        setAssetQuery(value);
        if (selectedVehicle && value !== selectedVehicle.name) {
            setSelectedVehicle(null);
        }
    }

    async function handleSubmit() {
        if (!selectedVehicle || !title.trim() || submitting) return;
        setSubmitting(true);
        setResult(null);

        try {
            const headers = apiHeaders;
            // Latest API version: polymorphic asset + summary + reported_at.
            // Falls back to the legacy vehicle_id/name payload for older-version tokens.
            let response = await fetch('https://secure.fleetio.com/api/v2/issues', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    asset_type: 'Vehicle',
                    asset_id: selectedVehicle.id,
                    summary: title.trim(),
                    description: description.trim(),
                    reported_at: new Date().toISOString()
                })
            });
            if (response.status === 400 || response.status === 403 || response.status === 422) {
                const fallback = await fetch('https://secure.fleetio.com/api/v2/issues', {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        vehicle_id: selectedVehicle.id,
                        name: title.trim(),
                        description: description.trim()
                    })
                });
                if (fallback.ok) response = fallback;
            }
            const data = await response.json().catch(() => null);

            if (response.ok) {
                const issueNumber = data && data.number ? String(data.number).replace(/#/g, '') : null;
                const issueUrl = issueNumber ? `https://secure.fleetio.com/${process.env.REACT_APP_FLEETIO_ACCOUNT_TOKEN}/issues/${issueNumber}` : null;

                // Also set the vehicle to Out of Service (unless it already is)
                let statusMessage = '';
                if (oosStatus && selectedVehicle.vehicle_status_id !== oosStatus.id) {
                    try {
                        const patchResponse = await fetch(`https://secure.fleetio.com/api/v1/vehicles/${selectedVehicle.id}`, {
                            method: 'PATCH',
                            headers,
                            body: JSON.stringify({ vehicle_status_id: oosStatus.id })
                        });
                        if (patchResponse.ok) {
                            statusMessage = ` ${selectedVehicle.name} is now ${oosStatus.name}.`;
                            if (onVehicleStatusChanged) {
                                onVehicleStatusChanged(selectedVehicle.id, oosStatus);
                            }
                        } else {
                            statusMessage = ` Issue saved, but the vehicle status could not be changed (HTTP ${patchResponse.status}).`;
                        }
                    } catch (patchError) {
                        console.error('Failed to update vehicle status', patchError);
                        statusMessage = ' Issue saved, but the vehicle status could not be changed (network error).';
                    }
                } else if (!oosStatus) {
                    statusMessage = ' Issue saved; no "Out of Service" status exists in this account, so the vehicle status was left unchanged.';
                }

                setResult({
                    type: 'success',
                    message: `Issue ${issueNumber ? '#' + issueNumber + ' ' : ''}created for ${selectedVehicle.name}.${statusMessage}`,
                    url: issueUrl
                });
                if (onIssueCreated && data) {
                    // Normalize to the shape the dashboard expects from the v2 issues list
                    onIssueCreated({
                        ...data,
                        number: issueNumber,
                        summary: data.summary || data.name || title.trim(),
                        asset: data.asset || data.vehicle || { name: selectedVehicle.name },
                        custom_fields: data.custom_fields || {}
                    });
                }
                setTitle('');
                setDescription('');
                setSelectedVehicle(null);
                setAssetQuery('');
            } else if (response.status === 403) {
                setResult({
                    type: 'error',
                    message: 'The Fleetio API key does not have permission to create issues. Ask an admin to grant "Issues" write access to the API key in Fleetio.'
                });
            } else {
                const detail = data && (data.detail || data.title);
                setResult({
                    type: 'error',
                    message: `Could not create the issue${detail ? ': ' + detail : ` (HTTP ${response.status})`}.`
                });
            }
        } catch (error) {
            console.error('Failed to create issue', error);
            setResult({ type: 'error', message: 'Network error - could not reach Fleetio.' });
        }

        setSubmitting(false);
    }

    if (!vehicles) {
        return <div className='loader-box'>
            <div className='loader'></div>
        </div>
    }

    return (
        <div className='field-report'>
            <div className='box field-card'>
                <div className='card-header'>
                    <h2>Report OOS Reason</h2>
                </div>
                <p className='field-hint'>
                    Taking an asset out of service? Log the reason here - it creates an open issue on the asset and sets its status to Out of Service.
                </p>

                <label className='field-label'>Asset</label>
                <div className='typeahead'>
                    <input
                        type='text'
                        placeholder='Search by vehicle number...'
                        value={assetQuery}
                        onChange={(e) => handleAssetChange(e.target.value)}
                        onFocus={() => setAssetFocused(true)}
                        onBlur={() => setAssetFocused(false)}
                    />
                    {assetFocused && !selectedVehicle && assetMatches.length > 0 && (
                        <ul className='typeahead-menu'>
                            {assetMatches.map((vehicle) => (
                                <li key={vehicle.id} onMouseDown={() => selectVehicle(vehicle)}>
                                    <span className='asset-name'>{vehicle.name}</span>
                                    <span className={`status-badge ${isOOS(vehicle) ? 'status-badge--oos' : ''}`}>
                                        {vehicle.vehicle_status_name}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <label className='field-label'>Reason (issue title)</label>
                <div className='typeahead'>
                    <input
                        type='text'
                        placeholder='e.g. Brake failure, Engine overheating...'
                        value={title}
                        onChange={(e) => { setTitle(e.target.value); setResult(null); }}
                        onFocus={() => setTitleFocused(true)}
                        onBlur={() => setTitleFocused(false)}
                    />
                    {titleFocused && titleSuggestions.length > 0 && (
                        <ul className='typeahead-menu'>
                            {titleSuggestions.map((name) => (
                                <li key={name} onMouseDown={() => setTitle(name)}>
                                    <span>{name}</span>
                                    <span className='suggestion-count'>used {titleCounts[name]}x</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <label className='field-label'>Details <span className='optional'>(optional)</span></label>
                <textarea
                    rows={3}
                    placeholder='Any extra details about the problem...'
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />

                <button
                    className='submit-issue-button'
                    disabled={!selectedVehicle || !title.trim() || submitting}
                    onClick={handleSubmit}
                >
                    {submitting ? 'Creating issue...' : 'Create Open Issue'}
                </button>

                {result && (
                    <div className={`result-banner result-banner--${result.type}`}>
                        {result.message}
                        {result.url && <> <a target='_blank' rel='noreferrer' href={result.url}>View in Fleetio</a></>}
                    </div>
                )}
            </div>
        </div>
    );
}
